/**
 * Global Zustand store for OMEN Web Tracker.
 * Holds auth state, selected flights, live data, display preferences, and UI state.
 */

import { create } from 'zustand';
import type { AuthUser, Flight, Message, Prediction, Separator, TeamPosition } from '../types/api';
import { DEFAULT_PREFS, loadPrefs, savePrefs, type TrackerPrefs } from '../utils/cookieStore';
import { injectFlightData } from '../tour/useTourMock';
import { colorForSlot } from '../constants/colors';

/** Number of color slots in the flight color palette (see `prefs.flightColors`). */
const COLOR_POOL_SIZE = 5;

export interface FlightPref {
  showNormal: boolean;
  showInstant: boolean;
  showTeams: boolean;
  isVisible: boolean;
}

interface AppStore {
  // Auth
  token: string | null;
  user: AuthUser | null;
  /** Store JWT token and decoded user payload. */
  setAuth(token: string, user: AuthUser): void;
  /** Clear auth state and remove token from localStorage. */
  logout(): void;

  // Available data (refreshed by slow cycle every 5 minutes)
  separators: Separator[];
  setSeparators(s: Separator[]): void;

  // Selected flights (max 5)
  selectedFlightIds: number[];
  flights: Record<number, Flight>;
  /** Add a flight to the selection and assign a display color slot. */
  selectFlight(flightId: number, flight: Flight): void;
  /** Remove a flight from the selection and free its color slot. */
  deselectFlight(flightId: number): void;

  // Flight → color slot association (stable across reordering/deselection of
  // other flights). Pool of available slot indices into prefs.flightColors.
  availableColorIndices: number[];
  flightColorIndex: Record<number, number>;

  // Per-flight live data
  messages: Record<number, Message[]>;
  teams: Record<number, TeamPosition[]>;
  predictions: Record<number, { normal?: Prediction; instant?: Prediction }>;

  setMessages(flightId: number, msgs: Message[]): void;
  setTeams(flightId: number, teams: TeamPosition[]): void;
  setPrediction(flightId: number, type: 'normal' | 'instant', pred: Prediction | null): void;

  // Per-flight display preferences
  flightPrefs: Record<number, FlightPref>;
  /** Update a single display preference key for a flight. */
  setFlightPref<K extends keyof FlightPref>(flightId: number, key: K, value: FlightPref[K]): void;

  // Global preferences (persisted to cookie)
  prefs: TrackerPrefs;
  /** Merge partial prefs update and persist to cookie. */
  setPrefs(prefs: Partial<TrackerPrefs>): void;

  // UI state
  isPanelOpen: boolean;
  setPanelOpen(open: boolean): void;
  activePanelFlightId: number | null;
  setActivePanelFlight(id: number | null): void;

  // "Selected historical position" — null means show the last known position
  selectedMessageIndex: Record<number, number | null>;
  setSelectedMessageIndex(flightId: number, idx: number | null): void;

  // Polling
  isPolling: boolean;
  lastUpdated: Date | null;
  setPollingState(polling: boolean, updated?: Date): void;

  // Tour demo mode — disables polling and skips auth checks
  isTourDemo: boolean;
  setTourDemo(v: boolean): void;
}

export const useAppStore = create<AppStore>((set, get) => ({
  // ── Auth ──────────────────────────────────────────────────────────────────
  token: localStorage.getItem('omen_token'),
  user: null,

  setAuth(token, user) {
    localStorage.setItem('omen_token', token);
    set({ token, user });
  },

  logout() {
    localStorage.removeItem('omen_token');
    set({
      token: null,
      user: null,
      selectedFlightIds: [],
      flights: {},
      messages: {},
      teams: {},
      predictions: {},
      flightPrefs: {},
      flightColorIndex: {},
      availableColorIndices: Array.from({ length: COLOR_POOL_SIZE }, (_, i) => i),
    });
  },

  // ── Separators ────────────────────────────────────────────────────────────
  separators: [],
  setSeparators: (separators) => set({ separators }),

  // ── Flight selection ──────────────────────────────────────────────────────
  selectedFlightIds: [],
  flights: {},

  selectFlight(flightId, flight) {
    const { selectedFlightIds, flights, flightPrefs, prefs, availableColorIndices, flightColorIndex } = get();
    if (selectedFlightIds.includes(flightId) || selectedFlightIds.length >= 5) return;
    if (availableColorIndices.length === 0) return; // no color slot left

    const [colorIdx, ...remaining] = availableColorIndices;

    set({
      selectedFlightIds: [...selectedFlightIds, flightId],
      flights: { ...flights, [flightId]: flight },
      flightPrefs: {
        ...flightPrefs,
        [flightId]: {
          showNormal: prefs.defaultShowNormal,
          showInstant: prefs.defaultShowInstant,
          showTeams: true,
          isVisible: true,
        },
      },
      availableColorIndices: remaining,
      flightColorIndex: { ...flightColorIndex, [flightId]: colorIdx },
    });
    // In demo mode, polling is disabled so mock data must be re-injected
    // explicitly whenever a flight is (re-)selected.
    if (get().isTourDemo) {
      injectFlightData(flightId);
    }
  },

  deselectFlight(flightId) {
    const { isTourDemo, selectedFlightIds, flights, messages, teams, predictions, flightPrefs, selectedMessageIndex, flightColorIndex, availableColorIndices } = get();
    const newFlights = { ...flights };
    const newPrefs = { ...flightPrefs };
    const newMsgIdx = { ...selectedMessageIndex };
    delete newFlights[flightId];
    delete newPrefs[flightId];
    delete newMsgIdx[flightId];

    const colorIdx = flightColorIndex[flightId];
    const newFlightColorIndex = { ...flightColorIndex };
    delete newFlightColorIndex[flightId];

    const update: Partial<AppStore> = {
      selectedFlightIds: selectedFlightIds.filter((id) => id !== flightId),
      flights: newFlights,
      flightPrefs: newPrefs,
      selectedMessageIndex: newMsgIdx,
      flightColorIndex: newFlightColorIndex,
      // Return the freed slot to the end of the pool (favors color variety
      // over immediate reuse of the just-freed slot).
      availableColorIndices: colorIdx !== undefined
        ? [...availableColorIndices, colorIdx]
        : availableColorIndices,
    };

    // In demo mode, preserve telemetry/team/prediction data so the user can
    // re-select a mock flight and still see its data (no polling to refetch it).
    if (!isTourDemo) {
      const newMessages = { ...messages };
      const newTeams = { ...teams };
      const newPredictions = { ...predictions };
      delete newMessages[flightId];
      delete newTeams[flightId];
      delete newPredictions[flightId];
      Object.assign(update, { messages: newMessages, teams: newTeams, predictions: newPredictions });
    }

    set(update);
  },

  availableColorIndices: Array.from({ length: COLOR_POOL_SIZE }, (_, i) => i),
  flightColorIndex: {},

  // ── Live data ─────────────────────────────────────────────────────────────
  messages: {},
  teams: {},
  predictions: {},

  setMessages(flightId, msgs) {
    const { messages } = get();
    const existing = messages[flightId] ?? [];
    // Merge existing + incoming, then sort ascending by ISO 8601 timestamp.
    // ISO 8601 strings compare correctly with localeCompare / < / > operators.
    const merged = [...existing, ...msgs];
    merged.sort((a, b) => (a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0));
    set({ messages: { ...messages, [flightId]: merged } });
  },

  setTeams(flightId, teams) {
    set((s) => ({ teams: { ...s.teams, [flightId]: teams } }));
  },

  setPrediction(flightId, type, pred) {
    const { predictions } = get();
    const current = predictions[flightId] ?? {};
    set({
      predictions: {
        ...predictions,
        [flightId]: { ...current, [type]: pred ?? undefined },
      },
    });
  },

  // ── Display preferences ───────────────────────────────────────────────────
  flightPrefs: {},

  setFlightPref(flightId, key, value) {
    const { flightPrefs } = get();
    const current = flightPrefs[flightId] ?? { showNormal: true, showInstant: false, showTeams: true, isVisible: true };
    set({ flightPrefs: { ...flightPrefs, [flightId]: { ...current, [key]: value } } });
  },

  // ── Global prefs (cookies) ────────────────────────────────────────────────
  prefs: loadPrefs() ?? DEFAULT_PREFS,

  setPrefs(partial) {
    const merged = { ...get().prefs, ...partial };
    if (!get().isTourDemo) savePrefs(merged);
    set({ prefs: merged });
  },

  // ── UI state ──────────────────────────────────────────────────────────────
  isPanelOpen: true,
  setPanelOpen: (isPanelOpen) => set({ isPanelOpen }),

  activePanelFlightId: null,
  setActivePanelFlight: (activePanelFlightId) => set({ activePanelFlightId }),

  selectedMessageIndex: {},
  setSelectedMessageIndex(flightId, idx) {
    set((s) => ({ selectedMessageIndex: { ...s.selectedMessageIndex, [flightId]: idx } }));
  },

  // ── Polling ───────────────────────────────────────────────────────────────
  isPolling: false,
  lastUpdated: null,

  setPollingState(isPolling, updated) {
    set({ isPolling, lastUpdated: updated ?? get().lastUpdated });
  },

  // ── Tour demo mode ────────────────────────────────────────────────────────
  isTourDemo: false,
  setTourDemo: (isTourDemo) => set({ isTourDemo }),
}));

/**
 * Returns the hex color assigned to a flight, resolved via `flightColorIndex`.
 * Stable across selection/deselection of other flights, and reflects
 * `prefs.flightColors` edits (Settings) immediately since it's read live.
 * Falls back to slot 0 if the flight has no assigned color (not selected).
 * @param flightId - flight to look up
 */
export function getFlightColor(flightId: number): string {
  const { flightColorIndex, prefs } = useAppStore.getState();
  const idx = flightColorIndex[flightId] ?? 0;
  return colorForSlot(idx, prefs.flightColors);
}
